/* eslint-disable prettier/prettier */
import { abEmailList, emailList } from "app/static/frontend/projects-new/list/action-creators"
import PropTypes from "prop-types"
import React, { Component, useEffect, useRef } from "react"
import { connect } from "react-redux"
import * as Sentry from "@sentry/browser"
import { checkHasPermission, checkHasRegion, checkPermission } from "shared/imports/permissionFunctions"
import {
    selectSearchIsTableView,
    selectSearchShouldHideFilters,
    shouldShowOutboundTexting,
} from "shared/userdata/userdata-selectors"
import swal from "sweetalert"

import { createTextFromSafedSearch, fetchInitialRecipients } from "app/static/frontend/outbound-texting/action-creators"
import { downloadSearchData, updateFilter } from "shared/search/action-creators"

import {
    selectAbbreviatedCurrentCount,
    selectAllActiveDeserializedFilters,
    selectAllActiveExcludeFilters,
    selectAllActiveSerializedFilters,
    selectCurrentCount,
    selectCurrentExactCount,
    selectFilterValue,
    selectIsCountLoading,
    selectIsExactCountInaccessible,
    selectShouldShowExactCount,
} from "shared/search/selectors"

import { SearchTextarea, SectionHeader } from "QuorumDesign"
import Tooltip from "app/static/frontend/components/Tooltip"
// eslint-disable-next-line
import {
    ALERT_FORM_KEY,
    EMAIL_DOWNLOAD_SWAL_CSV,
    EMAIL_DOWNLOAD_SWAL_CSV_DOCUMENTS,
    EMAIL_DOWNLOAD_SWAL_CSV_POST_ERROR,
    EMAIL_DOWNLOAD_ERROR_WORKFLOW,
    LOG_COMMENT_FORM_KEY,
    LOG_DONATION_FORM_KEY,
    LOG_INTERACTION_FORM_KEY,
    LOG_RELATIONSHIP_FORM_KEY,
    MAX_CSV_SIZE,
    MAX_WORD_SIZE,
} from "shared/imports/sharedConstants"

import { NoteVisualizationConsole } from "app/static/frontend/search-new/containers/Visualization/VisualizationConsole/VisualizationConsoles"
import swalContents from "app/static/frontend/swalConfigs"
import { isGrassrootsPersona } from "shared/grassroots/helperFunctions"
import {
    PRECISION_MIN_THRESHOLD,
    getDataLabelPlural,
    getDataLabelSingular,
    getPrecisionMaxThreshold,
} from "shared/imports/sharedHelperFunctions"
import { canDisplayBulkActionsConsole } from "shared/search/searchify/bulkactions/helperFunctions"
import { canViewPacReceipts } from "shared/pac/permissionHelpers"

// action creators
import { clearSavedSearch } from "app/static/frontend/alerts/action-creators"
import {
    toggleHideFilterSet,
    toggleSearchCountPrecision,
    toggleTableView,
} from "app/static/frontend/search-new/action-creators"
import {
    pushPage,
    destroyReduxForm,
    segueToBulkUploadContact,
    segueToEditAlert,
    segueToEditComment,
    segueToEditContact,
    segueToEditCustomOfficial,
    segueToEditDonation,
    segueToEditEvent,
    segueToEditGrassrootsSupporterAction,
    segueToEditInteraction,
    segueToEditOrganization,
    segueToEditRelationship,
    segueToNewIssueManagement,
    togglePayrollModal,
} from "global-action-creators"
import { createNewTemplateFromSearch } from "app/static/frontend/search-new/containers/Downloads/new-download-action-creators"
import { resourceDownloadAction, sendAction } from "shared/djangio/action-creators"
import { clearForm as clearLogInteractionForm } from "shared/forms/LogInteraction/action-creators"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"
import { updateUserdata } from "shared/userdata/userdata-action-creators"

import { AdvancedSearchFilter } from "shared/search/containers/Filter/Filters"
import ActionButton from "shared/search/containers/FilterableListView/ActionButtonContainer"

// imports for the display components!
import CalendarWrapper from "app/static/frontend/search-new/components/Visualization/Calendar/CalendarWrapper"
import AdvancedSearchNetworkGraph from "app/static/frontend/search/components/AdvancedSearchNetworkGraph"
import CreateAttachmentForm from "app/static/frontend/search/components/CreateAttachmentForm"
import CreateListForm from "app/static/frontend/search/components/CreateListForm"

import {
    getUrlParamsToInitializeForm,
    loggersForNoteFLV,
} from "app/static/frontend/forms/containers/LoggerFormHelperFunctions"

// selectors
import { selectIsStaffer } from "shared/profiles/official/official-profile-selectors"
import { selectBulkActionsCount, selectIsBulkActionsSubmittable } from "shared/search/searchify/bulkactions/selectors"
import { transformMoneyballRequest } from "shared/djangio/moneyballHelpers"
import isFeatureEnabled from "shared/featureflags/helperFunctions"

export default function genericActionButton(props) {
    let shouldShowButton = true

    if (Object.keys(props)?.length === 0 && props.constructor === Object) {
        shouldShowButton = false
    }

    if (props.regions) {
        // check to see whether user is in the proper region to see filter
        shouldShowButton = shouldShowButton && checkHasRegion(props.regions)
    }

    if (props.permissions) {
        // check to see whether user has the correct permissions to see filter
        shouldShowButton = shouldShowButton && checkHasPermission(props.permissions)
    }

    return (
        shouldShowButton && (
            <ActionButton
                callback={props.callback}
                dataCy={props.dataCy}
                uniqueActionButtonId={props.uniqueActionButtonId}
                displayComponentId={props.displayComponentId}
                label={props.label}
                activeLabel={props.activeLabel}
                icon={props.icon}
                {...props}
            />
        )
    )
}
genericActionButton.propTypes = {
    callback: PropTypes.func.isRequired,
    uniqueActionButtonId: PropTypes.string,
    displayComponent: PropTypes.string,
    permissions: PropTypes.arrayOf(PropTypes.number),
    regions: PropTypes.arrayOf(PropTypes.number),
}

// all buttons get uniqueFlvId, quorumDataType from SearchActionButtons.js

/* ****************************************************************************
 *
 * Creating objects (segueing to forms, alerts, etc)
 *
 **************************************************************************** */

const UnconnectedCreateEmailButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "createEmailButton",
        label: "Email",
        fa5_icon: "envelope",
        fa5_icon_family: "far",
        icon: "fa-envelope",
        permissions: [
            DjangIO.app.models.PermissionType.officials_outbox,
            DjangIO.app.models.PermissionType.custom_outbox,
        ],
        callback: async () => {
            const inputValue = await swal({
                title: "Add a title to remember your selection",
                content: {
                    element: "input",
                },
                buttons: {
                    cancel: true,
                    confirm: true,
                },
                closeOnClickOutside: false,
            })

            // if they hit cancel, do nothing.
            if (inputValue === null) {
                return
            }
            props.emailList({
                permanentFilters: [props.allActiveDeserializedFilters.toJS()],
                dataType: props.quorumDataType,
                name: inputValue,
            })
        },
        styleClassName: "search-create-email-button",
    })

const emailMapStateToProps = (state, props) => ({
    allActiveDeserializedFilters: selectAllActiveDeserializedFilters(state, props),
})

export const CreateEmailButton = connect(emailMapStateToProps, {
    emailList,
})(UnconnectedCreateEmailButton)

const UnconnectedCreateABEmailButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "createABEmailButton",
        label: "A/B Test",
        fa5_icon: "envelope",
        fa5_icon_family: "far",
        icon: "fa-envelope",
        permissions: [
            DjangIO.app.models.PermissionType.officials_outbox,
            DjangIO.app.models.PermissionType.custom_outbox,
        ],
        callback: async () => {
            const inputValue = await swal({
                title: "Add a title to remember your selection",
                content: {
                    element: "input",
                },
                buttons: {
                    cancel: true,
                    confirm: true,
                },
                closeOnClickOutside: false,
            })

            // if they hit cancel, do nothing.
            if (inputValue === null) {
                return
            }
            props.abEmailList({
                permanentFilters: [props.allActiveDeserializedFilters.toJS()],
                dataType: props.quorumDataType,
                name: inputValue,
            })
        },
        styleClassName: "search-create-email-button",
    })

const abEmailMapStateToProps = (state, props) => ({
    allActiveDeserializedFilters: selectAllActiveDeserializedFilters(state, props),
})

export const CreateABEmailButton = connect(abEmailMapStateToProps, {
    abEmailList,
})(UnconnectedCreateABEmailButton)

const UnconnectedCreateOrganizationButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "createOrganizationButton",
        permissions: [DjangIO.app.models.PermissionType.custom_contacts],
        label: "Create Organization",
        fa5_icon: "plus",
        fa5_icon_family: "far",
        icon: "fa-plus",
        callback: props.segueToEditOrganization,
        styleClassName: "add-contact-open-button",
    })

export const CreateOrganizationButton = connect(null, { segueToEditOrganization })(UnconnectedCreateOrganizationButton)

const UnconnectedCreateEventButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "createEventButton",
        permissions: [DjangIO.app.models.PermissionType.custom_events],
        label: "Create Event",
        fa5_icon: "plus",
        fa5_icon_family: "far",
        icon: "fa-plus",
        callback: props.segueToEditEvent,
        styleClassName: "add-contact-open-button",
    })

export const CreateEventButton = connect(null, { segueToEditEvent })(UnconnectedCreateEventButton)

const UnconnectedCreateAlertButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "createAlertButton",
        label: "Create Alert",
        fa5_icon: "rss",
        fa5_icon_family: "far",
        icon: "fa-rss",
        callback: () => {
            const kwargs = {
                user: DjangIO.app.userdata.models.QuorumUser.foreignKey(parseInt(Userdata.id)),
                data_type: props.quorumDataType,
                name: "",
                filters: [props.allActiveDeserializedFilters.toJS()],
            }

            props.clearSavedSearch()
            props.destroyReduxForm(ALERT_FORM_KEY)

            DjangIO.app.userdata.models.SafedSearch.objects
                .create(kwargs)
                .POST()
                .then((ss) => {
                    // then segue to alerts link
                    props.segueToEditAlert(props.quorumDataType, ss.id, true)
                })
        },
        tooltipText: "Alert Me When there are New Results and/or When the Current Results have Updates",
        styleClassName: "add-alert-open-button",
    })

const alertMapStateToProps = (state, props) => ({
    allActiveDeserializedFilters: selectAllActiveDeserializedFilters(state, props),
})

export const CreateAlertButton = connect(alertMapStateToProps, {
    segueToEditAlert,
    clearSavedSearch,
    destroyReduxForm,
})(UnconnectedCreateAlertButton)

const UnconnectedCreateContactButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "createContactButton",
        label: "Add a Contact",
        fa5_icon: "plus",
        fa5_icon_family: "far",
        icon: "fa-plus",
        callback: props.segueToEditContact,
    })

export const CreateContactButton = connect(null, { segueToEditContact })(UnconnectedCreateContactButton)

const UnconnectedCreateOfficialButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "createOfficialButton",
        label: "Create Official",
        fa5_icon: "plus",
        fa5_icon_family: "far",
        icon: "fa-plus",
        callback: props.segueToEditCustomOfficial,
    })

export const CreateOfficialButton = connect(null, { segueToEditCustomOfficial })(UnconnectedCreateOfficialButton)

const UnconnectedCreateGrassrootsSupporterActionButton = (props) => {
    return genericActionButton({
        ...props,
        uniqueActionButtonId: "createGrassrootsSupporterActionButton",
        label: "Add an Action",
        fa5_icon: "plus",
        fa5_icon_family: "far",
        icon: "fa-plus",
        callback: props.segueToEditGrassrootsSupporterAction,
    })
}

export const CreateGrassrootsSupporterActionButton = connect(null, { segueToEditGrassrootsSupporterAction })(
    UnconnectedCreateGrassrootsSupporterActionButton,
)

const UnconnectedCreateGrassrootsSupporterActionButtonPAC = (props) => {
    return genericActionButton({
        ...props,
        uniqueActionButtonId: "createGrassrootsSupporterActionButtonPAC",
        label: "Add points",
        callback: props.segueToEditGrassrootsSupporterAction,
    })
}

export const CreateGrassrootsSupporterActionButtonPAC = connect(null, { segueToEditGrassrootsSupporterAction })(
    UnconnectedCreateGrassrootsSupporterActionButtonPAC,
)

export const UnconnectedAddPayrollDeductionActionButton = (props) => {
    // Only render the button if user has the appropriate permissions
    if (isFeatureEnabled("ff_permissions_for_pac_2") && !canViewPacReceipts()) {
        return null
    }

    return genericActionButton({
        ...props,
        uniqueActionButtonId: "addPayrollDeductionActionButton",
        label: "Payroll Deduction",
        fa5_icon: "",
        fa5_icon_family: "",
        icon: "",
        callback: props.togglePayrollModal,
    })
}

export const AddPayrollDeductionActionButton = connect(null, { togglePayrollModal })(
    UnconnectedAddPayrollDeductionActionButton,
)

const UnconnectedBulkUploadContactsButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "bulkUploadContactsButton",
        permissions: [DjangIO.app.models.PermissionType.can_access_bulk_uploads],
        label: isGrassrootsPersona() ? "Bulk Upload" : "Bulk Upload Contacts",
        fa5_icon: "user-plus",
        fa5_icon_family: "far",
        icon: "fa-user-plus",
        callback: props.segueToBulkUploadContact,
    })

export const BulkUploadContactsButton = connect(null, { segueToBulkUploadContact })(UnconnectedBulkUploadContactsButton)

const UnconnectedCreateNoteButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "createNoteButton",
        permissions: [
            DjangIO.app.models.PermissionType.internal_relationship,
            DjangIO.app.models.PermissionType.external_relationship,
            DjangIO.app.models.PermissionType.external_interactions,
        ],
        label: "Add",
        fa5_icon: "plus",
        fa5_icon_family: "far",
        icon: "fa-plus",
        callback: () => null,
        styleClassName: "add-note-open-button",
    })

export const CreateNoteButton = connect(null, null)(UnconnectedCreateNoteButton)

/* Logger forms need to know what profile it is inside when seguing to the form so that it can pre-populate relevant fields */

const correctedQdtForLoggerForms = (profileQdt, profileId) =>
    selectIsStaffer(window.store.getState(), { profileId })
        ? DjangIO.app.models.QuorumDataType.staffer.value
        : profileQdt
const createNoteButtonConnection = (profileQdt, profileId) => () => ({
    profileId,
    profileQdt: correctedQdtForLoggerForms(profileQdt, profileId),
})
const createSegueToALoggerFormCallback =
    (props, formKey, segueFunc, isComment = false) =>
    () => {
        props.destroyReduxForm(formKey)
        props.clearLogInteractionForm()

        const urlParams = getUrlParamsToInitializeForm(props.profileQdt, props.profileId, isComment)
        segueFunc(urlParams, isComment)
    }

const UnconnectedAddInteractionButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "createInteractionButton",
        permissions: [
            DjangIO.app.models.PermissionType.internal_relationship,
            DjangIO.app.models.PermissionType.external_interactions,
        ],
        label: "Add Interaction",
        fa5_icon: "handshake",
        fa5_icon_family: "far",
        icon: "fa-handshake-o",
        callback: createSegueToALoggerFormCallback(props, LOG_INTERACTION_FORM_KEY, props.segueToEditInteraction),
        styleClassName: "add-note-open-button",
    })

export const createAddInteractionButton = (profileQdt, profileId) =>
    connect(createNoteButtonConnection(profileQdt, profileId), {
        clearLogInteractionForm,
        destroyReduxForm,
        segueToEditInteraction,
    })(UnconnectedAddInteractionButton)

const UnconnectedAddRelationshipButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "createRelationshipButton",
        permissions: [
            DjangIO.app.models.PermissionType.internal_relationship,
            DjangIO.app.models.PermissionType.external_relationship,
        ],
        label: "Create Relationship",
        fa5_icon: "exchange-alt",
        fa5_icon_family: "far",
        icon: "fa-exchange",
        callback: createSegueToALoggerFormCallback(props, LOG_RELATIONSHIP_FORM_KEY, props.segueToEditRelationship),
        styleClassName: "add-note-open-button",
    })

export const createAddRelationshipButton = (profileQdt, profileId) =>
    connect(createNoteButtonConnection(profileQdt, profileId), {
        clearLogInteractionForm,
        destroyReduxForm,
        segueToEditRelationship,
    })(UnconnectedAddRelationshipButton)

const UnconnectedAddCommentButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "createCommentButton",
        permissions: [DjangIO.app.models.PermissionType.internal_relationship],
        label: "Write Comment",
        fa5_icon: "edit",
        fa5_icon_family: "far",
        icon: "fa-pencil-square-o",
        callback: createSegueToALoggerFormCallback(props, LOG_COMMENT_FORM_KEY, props.segueToEditComment, true),
        styleClassName: "add-note-open-button",
    })

export const createAddCommentButton = (profileQdt, profileId) =>
    connect(createNoteButtonConnection(profileQdt, profileId), {
        clearLogInteractionForm,
        destroyReduxForm,
        segueToEditComment,
    })(UnconnectedAddCommentButton)

const UnconnectedAddDonationButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "createDonationButton",
        permissions: [
            DjangIO.app.models.PermissionType.internal_relationship,
            DjangIO.app.models.PermissionType.external_interactions,
        ],
        label: "Record Donation",
        fa5_icon: "gift",
        fa5_icon_family: "far",
        icon: "fa-gift",
        callback: createSegueToALoggerFormCallback(props, LOG_DONATION_FORM_KEY, props.segueToEditDonation),
        styleClassName: "add-note-open-button",
    })

export const createAddDonationButton = (profileQdt, profileId) =>
    connect(createNoteButtonConnection(profileQdt, profileId), {
        clearLogInteractionForm,
        destroyReduxForm,
        segueToEditDonation,
    })(UnconnectedAddDonationButton)

// downloads
const sendDownloadUserAction = (quorumDataType, optionLabel) => {
    const quorumDataTypeEnum = DjangIO.app.models.QuorumDataType.by_value(quorumDataType)

    return sendUserActionPost({
        userActionType: DjangIO.app.useractions.types.UserActionType.search_download,
        description: `${quorumDataTypeEnum.label} ${optionLabel}`,
        quorumDataType: quorumDataTypeEnum.value,
    })
}

const downloadMapStateToProps = (state, props) => ({
    currentCount: selectCurrentCount(state, props),
    // for use in DjangIO.filter().GET()
    allActiveSerializedFilters: selectAllActiveSerializedFilters(state, props),
    // for use in anything that needs the objects in the filters,
    // not just serialization. This is for anything that gets put into a safed search.
    allActiveDeserializedFilters: selectAllActiveDeserializedFilters(state, props),
    allActiveDefaultExcludeFilters: selectAllActiveExcludeFilters(state, props),
})

const injectCampaignFinanceParams = (dataType, params, downloadSize, isDeserialized) => {
    let filter

    if (dataType === DjangIO.app.models.QuorumDataType.receipt.value) {
        filter = transformMoneyballRequest(params, DjangIO.app.moneyball.models.Receipt)
        filter.page_size = downloadSize
        filter.moneyball_params.page_size = downloadSize
        if (isDeserialized) {
            filter = filter.moneyball_params
        }
    } else if (dataType === DjangIO.app.models.QuorumDataType.disbursement.value) {
        filter = transformMoneyballRequest(params, DjangIO.app.moneyball.models.Disbursement)
        filter.page_size = downloadSize
        filter.moneyball_params.page_size = downloadSize
        if (isDeserialized) {
            filter = filter.moneyball_params
        }
    } else {
        filter = params
    }

    return filter
}

const UnconnectedDownloadSpreadsheetButton = (props) => {
    const pluralObjects = DjangIO.app.models.QuorumDataType.by_value(props.quorumDataType).plural
    const isDocumentDownload = props.resource.objects.model === DjangIO.app.document.models.NewDocument

    const directDownloadCallback = async () => {
        const willDownload = isDocumentDownload
            ? await swal(swalContents.willDownloadDocuments)
            : await swal(
                  Object.assign(swalContents.willDownload, {
                      text: `This will download ${props.currentCount} ${pluralObjects}`,
                  }),
              )

        // SERIALIZED filters, not deserialized!
        const filters = injectCampaignFinanceParams(
            props.quorumDataType,
            props.allActiveSerializedFilters.toJS(),
            MAX_CSV_SIZE,
            false,
        )

        if (isDocumentDownload) {
            filters.exclude_youtube_videos = "true"
        }

        // if the promise resolves to true, proceed with download
        if (willDownload) {
            await props.resource.objects
                .filter(filters)
                .exclude(props.allActiveDefaultExcludeFilters.toJS())
                .csv()
                .download()

            sendDownloadUserAction(
                DjangIO.app.models.QuorumDataType.by_value(props.quorumDataType).value,
                "Download Spreadsheet",
            )

            // if all goes well, the download dialogue will show up after promise resolves
            swal(Object.assign(swalContents.successDownload))
        }
    }
    const emailDownloadCallback = async (swalOverride) => {
        //  If the size of the queryset exceeds the set join limit, give the user the option to
        //  get an email with a link to a spreadsheet of the set of rows in the queryset
        //  within the organization's limit for csv rows.

        // Download a larger spreadsheet to email instead
        swal(isDocumentDownload ? EMAIL_DOWNLOAD_SWAL_CSV_DOCUMENTS : swalOverride ?? EMAIL_DOWNLOAD_SWAL_CSV).then(
            (inputValue) => {
                if (inputValue === null) {
                    return
                }
                if (inputValue === "") {
                    swal("Error", "Please enter a name.", "error")
                    return false
                }

                // For contact downloads, there isn't a max csv size enforced
                const text =
                    props.quorumDataType === DjangIO.app.models.QuorumDataType.supporter.value ||
                    props.currentCount < Userdata.max_csv_size
                        ? `Check your inbox in the next few minutes for a link for the ${props.currentCount} ${pluralObjects}.`
                        : `Check your inbox in the next few minutes for a link for the first ${Userdata.max_csv_size} ${pluralObjects}.`

                swal({
                    icon: "info",
                    title: "Email Pending",
                    text,
                })

                const csvInstructions = {
                    filter: injectCampaignFinanceParams(
                        props.quorumDataType,
                        props.allActiveDeserializedFilters.toJS(),
                        Userdata.max_csv_size,
                        true,
                    ),
                    qdt: props.quorumDataType,
                }

                window.redirectCSVDownload(csvInstructions, inputValue)
            },
        )
    }
    return genericActionButton({
        ...props,
        uniqueActionButtonId: "downloadSpreadsheetButton",
        label: "Download Spreadsheet",
        fa5_icon: "table",
        fa5_icon_family: "far",
        icon: "fa-table",
        callback: async () => {
            try {
                if (!props.currentCount) {
                    swal(swalContents.emptyDownload)
                } else if (props.currentCount >= MAX_CSV_SIZE) {
                    emailDownloadCallback()
                } else {
                    directDownloadCallback()
                }
            } catch (err) {
                const response = await swal(EMAIL_DOWNLOAD_ERROR_WORKFLOW)
                if (response) {
                    emailDownloadCallback(EMAIL_DOWNLOAD_SWAL_CSV_POST_ERROR)
                }
                Sentry.captureException(err)
            }
        },
    })
}

export const DownloadSpreadsheetButton = connect(downloadMapStateToProps, null)(UnconnectedDownloadSpreadsheetButton)

const UnconnectedDownloadWordButton = (props) => {
    const pluralObjects = DjangIO.app.models.QuorumDataType.by_value(props.quorumDataType).plural

    return genericActionButton({
        ...props,
        uniqueActionButtonId: "downloadWordButton",
        label: "Download Word (.doc)",
        fa5_icon: "file-word",
        fa5_icon_family: "far",
        icon: "fa-file-word-o",
        callback: async () => {
            try {
                if (!props.currentCount) {
                    swal(swalContents.emptyDownload)
                } else {
                    const isDocumentDownload = props.resource.objects.model === DjangIO.app.document.models.NewDocument
                    const text =
                        props.currentCount >= MAX_WORD_SIZE
                            ? `This will download the first ${MAX_WORD_SIZE} ${pluralObjects}`
                            : `This will download ${props.currentCount} ${pluralObjects}`

                    // wait for the users input to confirm download
                    const willDownload = isDocumentDownload
                        ? await swal(swalContents.willDownloadDocuments)
                        : await swal(Object.assign(swalContents.willDownload, { text }))

                    // SERIALIZED filters, not deserialized!
                    const filters = injectCampaignFinanceParams(
                        props.quorumDataType,
                        props.allActiveSerializedFilters.toJS(),
                        MAX_WORD_SIZE,
                        false,
                    )

                    if (isDocumentDownload) {
                        filters.exclude_youtube_videos = "true"
                    }

                    // if the promise resolves to true, proceed with download
                    if (willDownload) {
                        await props.resource.objects.filter(filters).docx().download()

                        sendDownloadUserAction(
                            DjangIO.app.models.QuorumDataType.by_value(props.quorumDataType).value,
                            "Download Word",
                        )

                        // if all goes well, the download dialogue will show up after promise resolves
                        swal(Object.assign(swalContents.successDownload))
                    }
                    return
                }
            } catch (err) {
                swal(swalContents.downloadErrorDirectToCSV)
                Sentry.captureException(err)
            }
        },
    })
}

export const DownloadWordButton = connect(downloadMapStateToProps, null)(UnconnectedDownloadWordButton)

const UnconnectedDownloadExcelButton = (props) => {
    const pluralObjects = DjangIO.app.models.QuorumDataType.by_value(props.quorumDataType).plural

    return genericActionButton({
        ...props,
        uniqueActionButtonId: "downloadExcelButton",
        label: "Download Excel (.xlsx)",
        fa5_icon: "file-excel",
        fa5_icon_family: "far",
        icon: "fa-file-excel-o",
        callback: async () => {
            try {
                if (!props.currentCount) {
                    swal(swalContents.emptyDownload)
                } else {
                    const isDocumentDownload = props.resource.objects.model === DjangIO.app.document.models.NewDocument
                    const text =
                        props.currentCount >= Userdata.max_csv_size
                            ? `This will download the first ${Userdata.max_csv_size} ${pluralObjects}`
                            : `This will download ${props.currentCount} ${pluralObjects}`

                    // wait for the users input to confirm download
                    const willDownload = isDocumentDownload
                        ? await swal(swalContents.willDownloadDocuments)
                        : await swal(Object.assign(swalContents.willDownload, { text }))

                    // SERIALIZED filters, not deserialized!
                    const filters = injectCampaignFinanceParams(
                        props.quorumDataType,
                        props.allActiveSerializedFilters.toJS(),
                        Userdata.max_csv_size,
                        false,
                    )

                    if (isDocumentDownload) {
                        filters.exclude_youtube_videos = "true"
                    }

                    // if the promise resolves to true, proceed with download
                    if (willDownload) {
                        await props.resource.objects
                            .filter(filters)
                            .exclude(props.allActiveDefaultExcludeFilters.toJS())
                            .xlsx()
                            .download()

                        sendDownloadUserAction(
                            DjangIO.app.models.QuorumDataType.by_value(props.quorumDataType).value,
                            "Download Excel",
                        )

                        // if all goes well, the download dialogue will show up after promise resolves
                        swal(Object.assign(swalContents.successDownload))
                    }
                    return
                }
            } catch (err) {
                swal(swalContents.downloadErrorDirectToCSV)
                Sentry.captureException(err)
            }
        },
    })
}

export const DownloadExcelButton = connect(downloadMapStateToProps, null)(UnconnectedDownloadExcelButton)

const UnconnectedDownloadSearchPDFButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "downloadSearchPDFButton",
        label: "Download PDF",
        fa5_icon: "file-pdf",
        fa5_icon_family: "far",
        icon: "fa-file-pdf-o",
        callback: async () => {
            const isDocumentDownload = props.resource.objects.model === DjangIO.app.document.models.NewDocument
            const filters = props.allActiveDeserializedFilters.toJS()

            if (isDocumentDownload) {
                filters.exclude_youtube_videos = true
            }

            try {
                await props.createNewTemplateFromSearch({
                    filterQdt: props.quorumDataType,
                    filters: filters,
                    filterElementCount: props.currentCount,
                    isDocumentDownload,
                })
            } catch (err) {
                swal(swalContents.downloadError)
                console.error(err)
                Sentry.captureException(err)
            }
        },
    })

export const DownloadSearchPDFButton = connect(downloadMapStateToProps, { createNewTemplateFromSearch })(
    UnconnectedDownloadSearchPDFButton,
)

const UnconnectedDownloadGroupByCustomFields = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "downloadGroupedByCustomFields",
        label: "Download MEC/LEC Report",
        fa5_icon: "file-pdf",
        fa5_icon_family: "far",
        icon: "fa-file-pdf-o",
        callback: async () => {
            const willDownload = await swal(swalContents.willDownload)

            if (willDownload) {
                await resourceDownloadAction(
                    DjangIO.resourceFromDataType(props.quorumDataType),
                    "download_grouped_by_custom_fields",
                    props.allActiveDeserializedFilters.toJS(),
                )

                swal(Object.assign(swalContents.successDownload))
            }
            return
        },
    })

export const DownloadGroupByCustomFieldButton = connect(
    downloadMapStateToProps,
    {},
)(UnconnectedDownloadGroupByCustomFields)

const UnconnectedSubscribeToCalendarButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "subscribeToCalendarButton",
        label: "Subscribe to Calendar",
        fa5_icon: "rss",
        fa5_icon_family: "far",
        icon: "fa-rss",
        callback: () => {
            const kwargs = {
                user: DjangIO.app.userdata.models.QuorumUser.foreignKey(parseInt(Userdata.id)),
                data_type: props.quorumDataType,
                name: "Quorum Calendar",
                filters: [props.allActiveDeserializedFilters.toJS()],
            }

            return DjangIO.app.userdata.models.SafedSearch.objects
                .create(kwargs)
                .POST()
                .then((ss) => {
                    let url = `webcal://${window.location.host}`
                    url += "/api/search/calendar_download/"
                    url += `?reference=${ss.reference_hash}`

                    const span = document.createElement("span")
                    span.innerHTML = `We're trying to download the calendar feed to your computer.
                    Your calendar application should open automatically and subscribe to the
                    calendar; however, if that doesn't happen, you can also subscribe manually.
                    Your calendar subscription link is:<br><br>${url}<br><br>You can find
                    instructions on how to add a calendar subscription for Outlook
                    <a target="_BLANK" href="https://support.office.com/en-us/article/Import-or-subscribe-to-a-calendar-in-Outlook-com-or-Outlook-on-the-web-CFF1429C-5AF6-41EC-A5B4-74F2C278E98C">here</a> and for Google Calendar <a href="https://support.google.com/calendar/answer/37100?co=GENIE.Platform%3DDesktop&hl=en">here</a>.`

                    swal({
                        icon: "info",
                        title: "Downloading Calendar Feed",
                        content: span,
                    })

                    return (window.location.href = url)
                })
        },
    })

const subscribeToCalendarMapStateToProps = (state, props) => ({
    allActiveDeserializedFilters: selectAllActiveDeserializedFilters(state, props),
})
export const SubscribeToCalendarButton = connect(
    subscribeToCalendarMapStateToProps,
    null,
)(UnconnectedSubscribeToCalendarButton)

const UnconnectedDownloadVCardButton = (props) => {
    const pluralObjects = DjangIO.app.models.QuorumDataType.by_value(props.quorumDataType).plural

    return genericActionButton({
        ...props,
        uniqueActionButtonId: "downloadVCardButton",
        label: "Download to Contacts",
        fa5_icon: "download",
        fa5_icon_family: "far",
        icon: "fa-download",
        callback: async () => {
            try {
                if (!props.currentCount) {
                    swal(swalContents.emptyDownload)
                } else {
                    const text =
                        props.currentCount >= Userdata.max_csv_size
                            ? `This will download the first ${Userdata.max_csv_size} ${pluralObjects}`
                            : `This will download ${props.currentCount} ${pluralObjects}`

                    // wait for the users input to confirm download
                    const willDownload = await swal(Object.assign(swalContents.willDownload, { text }))

                    // if the promise resolves to true, proceed with download
                    if (willDownload) {
                        sendDownloadUserAction(
                            DjangIO.app.models.QuorumDataType.by_value(props.quorumDataType).value,
                            "Download vCard to Contacts",
                        )

                        // unfortunately the endpoint doesn't resolve the promise correctly, so this will do for now
                        swal(Object.assign(swalContents.downloadInProgress))
                        props.downloadSearchData({
                            uniqueFlvId: props.uniqueFlvId,
                            resource: props.resource,
                            actions: { bulkVCF: true },
                        })
                    }
                    return
                }
            } catch (err) {
                swal(swalContents.downloadError)
                Sentry.captureException(err)
            }
        },
    })
}

export const DownloadVCardButton = connect(downloadMapStateToProps, { downloadSearchData })(
    UnconnectedDownloadVCardButton,
)

const UnconnectedNewIssueButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "newIssueButton",
        hasDisplayComponent: false,
        label: props.issueId ? "Create Sub-Issue" : "Create Issue",
        fa5_icon: "thumbtack",
        fa5_icon_family: "far",
        icon: "fa-thumb-tack",
        tooltipText: props.issueId ? "Create a new Issue and set this Issue as its parent" : "Create a new Issue",
        styleClassName: "create-issue-open-button",
        callback: () => {
            props.segueToNewIssueManagement(props.issueId)
        },
    })

export const createNewIssueButton = (profileId) =>
    connect(() => ({ issueId: profileId }), { segueToNewIssueManagement })(UnconnectedNewIssueButton)

// Displays! Anything that causes a menu to dropdown.
export class DisplayVisualizationsButton extends Component {
    static defaultProps = {
        uniqueActionButtonId: "displayVisualizationsButton",
        hasDisplayComponent: true,
        label: "Show Visualizations",
        activeLabel: "Hide Visualizations",
        fa5_icon: "chart-line",
        fa5_icon_family: "far",
        icon: "fa-line-chart",
        tooltipText: "Display Visualizations",
        styleClassName: "visualizations",
        shortLabel: "Visualizations",
        callback: () => null,
    }

    render() {
        return genericActionButton(this.props)
    }
}

export class DisplayCalendarButton extends Component {
    static defaultProps = {
        uniqueActionButtonId: "displayCalendarButton",
        hasDisplayComponent: true,
        label: "Show Calendar",
        activeLabel: "Hide Calendar",
        fa5_icon: "calendar-alt",
        fa5_icon_family: "far",
        icon: "fa-calendar",
        tooltipText: "Add Results to Your Calendar",
        styleClassName: "calendar",
        callback: () => null,
    }

    render() {
        return genericActionButton(this.props)
    }
}

export class DisplayNetworkGraphButton extends Component {
    static defaultProps = {
        uniqueActionButtonId: "displayNetworkGraphButton",
        hasDisplayComponent: true,
        label: "Show Network",
        activeLabel: "Hide Network",
        fa5_icon: "project-diagram",
        fa5_icon_family: "far",
        icon: "fa-connectdevelop",
        tooltipText: "View Graph of Relationships",
        callback: () => null,
    }

    render() {
        return genericActionButton(this.props)
    }
}

export class SaveToListButton extends Component {
    static defaultProps = {
        uniqueActionButtonId: "saveToListButton",
        hasDisplayComponent: true,
        label: "Save List",
        activeLabel: "Cancel",
        fa5_icon: "thumbtack",
        fa5_icon_family: "far",
        icon: "fa-thumb-tack",
        tooltipText: "Save an Auto-Updating List of Results",
        styleClassName: "create-list",
        callback: () => null,
    }

    render() {
        return genericActionButton(this.props)
    }
}

const UnconnectedShowFilterSetButton = (props) =>
    genericActionButton({
        ...props,
        dataCy: "filter-view-show-button",
        uniqueActionButtonId: "showFilterSetButton",
        label: "Show Filters",
        fa5_icon: "arrow-from-left",
        fa5_icon_family: "far",
        callback: () => {
            toggleHideFilterSet({
                sendAction: props.sendAction,
                shouldHideFilters: !props.shouldHideFilters,
                updateUserdata: props.updateUserdata,
            })
        },
    })
const showFilterSetButtonMapStateToProps = (state, props) => ({
    shouldHideFilters: selectSearchShouldHideFilters(state, props),
})

export const ShowFilterSetButton = connect(showFilterSetButtonMapStateToProps, {
    updateUserdata,
    sendAction,
})(UnconnectedShowFilterSetButton)

const UnconnectedTableViewButton = (props) => {
    const isOnlyTableView = props.resource.data_model.includes("moneyball") || props.isOnlyTableView
    const isOnlyCardView =
        props.quorumDataType === DjangIO.app.models.QuorumDataType.note.value &&
        window.location.search.includes("?id__in=")

    if (isOnlyTableView || isOnlyCardView) {
        toggleTableView({
            isTableView: isOnlyTableView || !isOnlyCardView,
            sendAction: props.sendAction,
            updateUserdata: props.updateUserdata,
        })
    }

    return genericActionButton({
        ...props,
        uniqueActionButtonId: "tableViewButton",
        hasDisplayComponent: false,
        isToggle: true,
        selectedIndex: props.isTableView ? 1 : 0,
        menuOptions:
            isOnlyTableView || isOnlyCardView
                ? []
                : [
                      {
                          dataCy: "card-view-toggle",
                          text: "Cards",
                          icon: "cards",
                          iconFamily: "q",
                          onClick: () =>
                              props.isTableView &&
                              toggleTableView({
                                  isTableView: false,
                                  sendAction: props.sendAction,
                                  updateUserdata: props.updateUserdata,
                              }),
                      },
                      {
                          dataCy: "table-view-toggle",
                          text: "Table",
                          icon: "table",
                          iconFamily: "fal",
                          onClick: () =>
                              !props.isTableView &&
                              toggleTableView({
                                  isTableView: true,
                                  sendAction: props.sendAction,
                                  updateUserdata: props.updateUserdata,
                              }),
                      },
                  ],
        callback: () => null,
    })
}

const tableViewButtonMapStateToProps = (state, props) => ({
    isTableView: selectSearchIsTableView(state, props),
})

export const TableViewButton = connect(tableViewButtonMapStateToProps, {
    updateUserdata,
    sendAction,
})(UnconnectedTableViewButton)

export class AddFileButton extends Component {
    static defaultProps = {
        uniqueActionButtonId: "addFileButton",
        hasDisplayComponent: true,
        label: "Add File",
        activeLabel: "Cancel",
        fa5_icon: "plus",
        fa5_icon_family: "far",
        icon: "fa-plus",
        tooltipText: "Upload a file to an issue",
        styleClassName: "add-upload-open-button",
        callback: () => null,
    }

    render() {
        return genericActionButton(this.props)
    }
}

class UnconnectedBulkActionsButton extends Component {
    static defaultProps = {
        dataCy: "bulk-actions-advanced-search-button",
        uniqueActionButtonId: "bulkActionsButton",
        hasDisplayComponent: true,
        label: "Apply Bulk Actions",
        activeLabel: "Hide Bulk Actions",
        fa5_icon: "bolt",
        fa5_icon_family: "far",
        icon: "fa-bolt",
        tooltipText: "Apply an Action to All Items in this Search",
        styleClassName: "bulk-callbacks",
        shortLabel: "Bulk Actions",
        callback: () => null,
    }

    render() {
        // TODO: support bulk actions in table view
        // for the time being, we don't want to show the bulk actions button in table view
        return !this.props.isTableView && genericActionButton(this.props)
    }
}

export const BulkActionsButton = connect(tableViewButtonMapStateToProps, null)(UnconnectedBulkActionsButton)

// groups
export const createDownloadButtonGroup = (buttons) => ({
    buttons,
    fa5_icon: "download",
    fa5_icon_family: "far",
    icon: "fa-cloud-download",
    label: "Download",
    tooltipText: "Download",
})

export const NoteViewsButtonGroup = {
    buttons: [
        ...(NoteVisualizationConsole ? [DisplayVisualizationsButton] : []),
        DisplayCalendarButton,
        DisplayNetworkGraphButton,
    ],
    fa5_icon_family: "far",
    fa5_icon: "chart-line",
    icon: "fa-line-chart",
    label: "Views",
    key: "noteViewsButtonGroup",
    dataCy: "noteViewsButtonGroup",
    tooltipText: "Views",
}

export const GroupedActionsButtonGroup = {
    buttons: [CreateAlertButton, SaveToListButton, ...(canDisplayBulkActionsConsole() ? [BulkActionsButton] : [])],
    dataCy: "actions-button-group",
    fa5_icon: "rss",
    fa5_icon_family: "far",
    icon: "fa-rss",
    label: "Actions",
    key: "noteActionsButtonGroup",
    tooltipText: "Actions",
}

export const GrassrootsActionsButtonGroup = {
    buttons: [
        CreateGrassrootsSupporterActionButton,
        SaveToListButton,
        ...(canDisplayBulkActionsConsole() ? [BulkActionsButton] : []),
    ],
    fa5_icon: "rss",
    fa5_icon_family: "far",
    icon: "fa-rss",
    label: "Actions",
    key: "grassrootsActionsButtonGroup",
    tooltipText: "Actions",
}

export const AddGrassrootsSupporterButtonGroup = {
    buttons: [CreateGrassrootsSupporterActionButtonPAC, AddPayrollDeductionActionButton],
    fa5_icon: "plus",
    fa5_icon_family: "far",
    icon: "fa-plus",
    label: "Add an action",
    key: "addGrassrootsSupporterButtonGroup",
    tooltipText: "Add an action",
}

export const PublicOrganizationActionsButtonGroup = {
    buttons: [
        CreateOrganizationButton,
        SaveToListButton,
        ...(canDisplayBulkActionsConsole() ? [BulkActionsButton] : []),
    ],
    fa5_icon: "rss",
    fa5_icon_family: "far",
    icon: "fa-rss",
    label: "Actions",
    key: "publicOrganizationActionsButtonGroup",
    tooltipText: "Actions",
}

export const AdvocatesButtonGroup = {
    buttons: [
        CreateAlertButton,
        SaveToListButton,
        CreateContactButton,
        BulkUploadContactsButton,
        DownloadSpreadsheetButton,
        DownloadExcelButton,
        DownloadWordButton,
        DownloadSearchPDFButton,
        DownloadVCardButton,
    ],
    dataCy: "advocates-button-group",
    label: "More Actions",
    key: "moreActionsButtonGroup",
    tooltipText: "Take More Actions",
}

const UnconnectedAdvocatesSendTextMessageButton = (props) =>
    genericActionButton(
        shouldShowOutboundTexting() && checkPermission(DjangIO.app.models.PermissionType.can_access_grassroots)
            ? {
                  ...props, // Needed for button UI integrity
                  uniqueActionButtonId: "advocatesSendTextMessageButton",
                  label: "Text",
                  activeLabel: "Text",
                  fa5_icon: "phone",
                  fa5_icon_family: "fas",
                  icon: "fa-phone",
                  tooltipText: "SMS text subscribed advocates",
                  hasDisplayComponent: false,
                  styleClassName: "advocates-send-text-message-button-style",
                  shortLabel: "Text",
                  callback: async () => {
                      const inputValue = await swal({
                          title: "Add a title to remember your selection",
                          content: {
                              element: "input",
                          },
                          buttons: {
                              cancel: true,
                              confirm: true,
                          },
                          closeOnClickOutside: false,
                      })

                      // if they hit cancel, do nothing.
                      if (inputValue === null) {
                          return
                      }

                      // remove dehydrate_extra from activeFilters
                      const { dehydrate_extra, ...activeFilters } = props.activeFilters.toJS() // eslint-disable-line
                          ? props.activeFilters.toJS()
                          : {}

                      // add permanent filters
                      const grassrootsPermanentFilters = {
                          has_text_subscribed: true,
                      }

                      // retrieve the contacts for the initial filters
                      const data = await props.fetchInitialRecipients({
                          ...activeFilters,
                          ...grassrootsPermanentFilters,
                          limit: 0,
                      })

                      // create the initial contact recipients with the retrieved contacts and
                      // configure searchify to accept initial values (initial added_ids and filters)
                      const initialConfiguration = {
                          recipient_selection: {
                              added_ids: data["objects"].map((item) => item.id),
                              deleted_ids: [],
                              estimated_count: data["objects"].length,
                              filters: [{ ...activeFilters }],
                              permanent_filter: grassrootsPermanentFilters,
                              set_searchify_initial_values: true,
                          },
                      }

                      props.createTextFromSafedSearch(initialConfiguration, inputValue)
                  },
              }
            : {},
    )
const AdvocatesSendTextMessageButtonMapStateToProps = (state, props) => ({
    activeFilters: selectAllActiveDeserializedFilters(state, props),
})
export const AdvocatesSendTextMessageButton = connect(AdvocatesSendTextMessageButtonMapStateToProps, {
    fetchInitialRecipients,
    createTextFromSafedSearch,
})(UnconnectedAdvocatesSendTextMessageButton)

export const AdvocatesMessageGroup = {
    buttons: [CreateEmailButton, CreateABEmailButton, AdvocatesSendTextMessageButton],
    dataCy: "advocates-message-button-group",
    label: "Message",
    key: "advocatesMessageGroup",
    tooltipText: "Message Advocates",
    fa5_icon: "envelope",
    fa5_icon_family: "far",
    icon: "fa-envelope",
}

export const createOrderByButtonGroup = ({ qdt, uniqueFilterId }) => ({
    buttons: [
        ...(DjangIO.app.search.types.QuorumDataTypeOrdering.by_data_type[qdt].keys.map((quorumDataTypeOrderingKey) =>
            orderByDropdownListItem({
                enumeration: DjangIO.app.search.types.QuorumDataTypeOrdering[quorumDataTypeOrderingKey],
                uniqueFilterId,
            }),
        ) || []),
    ],
    dataCy: "order-by-button",
    fa5_icon: "sort-amount-down",
    fa5_icon_family: "far",
    icon: "fa-sort-amount-desc ",
    label: "Sort By",
    key: "sortByButton",
    tooltipText: "Sort By",
})

export const UnconnectedOrderByDropdownListItem = (props) =>
    genericActionButton({
        ...props,
        dataCy: props.enumeration.key,
        dropdownListItemIsActive:
            props.filterValue === props.enumeration.value ||
            // if we have not set a props.filterValue, select the default ordering option
            (!props.filterValue && props.enumeration.default),
        // override full enumeration entry spread in ...props above
        enumeration: undefined,
        label: props.enumeration.label,
        uniqueActionButtonId: props.uniqueFilterId,
        callback: () => {
            // don't fire unnecessary props.updateFilter
            // if the user clicks on "Default" while already sorting by default
            if (props.enumeration.value || props.filterValue) {
                props.updateFilter(
                    props.uniqueFilterId,
                    props.uniqueFlvId,
                    props.enumeration.value,
                    Boolean(props.enumeration.value),
                )
            }
        },
    })
UnconnectedOrderByDropdownListItem.defaultProps = {
    enumeration: {
        label: "Default",
        value: null,
    },
}

export const orderByDropdownListItem = ({ enumeration, uniqueFilterId }) =>
    connect(
        (state, props) => ({
            filterValue: selectFilterValue(state, {
                uniqueFlvId: props.uniqueFlvId,
                uniqueFilterId,
            }),
            enumeration,
            uniqueFilterId,
        }),
        {
            updateFilter,
        },
    )(UnconnectedOrderByDropdownListItem)

export const AddContactButtonGroup = {
    buttons: [CreateContactButton, BulkUploadContactsButton],
    dataCy: "AddContactButtonGroup",
    fa5_icon: "plus",
    fa5_icon_family: "far",
    icon: "fa-plus",
    label: "Create",
    styleClassName: "contact-dropdown",
}

export const createAddNoteButtonGroup = (props) => {
    const qdtToPopulateOnSegue = correctedQdtForLoggerForms(props.profileQdt, props.profileId)
    // eslint-disable-next-line
    const [field, loggersAvailable] = loggersForNoteFLV(
        qdtToPopulateOnSegue,
        props.profileId,
        props.uniqueFlvId === "NoteDefaultSearch",
    )
    const buttons = [
        loggersAvailable.interaction && createAddInteractionButton(qdtToPopulateOnSegue, props.profileId),
        loggersAvailable.relationship && createAddRelationshipButton(qdtToPopulateOnSegue, props.profileId),
        loggersAvailable.comment && createAddCommentButton(qdtToPopulateOnSegue, props.profileId),
        loggersAvailable.donation && createAddDonationButton(qdtToPopulateOnSegue, props.profileId),
    ].filter((button) => Boolean(button))

    // if there is only one logger available (e.g. documents only have comments), then just show the single button instead of a dropdown
    if (buttons.length === 1) {
        return buttons[0]
    }

    // otherwise return the dropdown
    return {
        buttons,
        fa5_icon: "plus",
        fa5_icon_family: "far",
        icon: "fa-plus",
        label: "Add",
        styleClassName: "add-note-dropdown",
        tooltipText: "Add Note",
    }
}

const VisualizationConsoleWrapper = (props) => {
    const VisualizationConsole = props.visualizationConsole
    return <VisualizationConsole {...props} />
}

const BulkActionsConsoleWrapper = (props) => {
    const BulkActionsConsole = props.bulkActionsConsole
    return <BulkActionsConsole {...props} />
}

export const buttonToComponentMapping = {
    [DisplayVisualizationsButton.defaultProps.uniqueActionButtonId]: VisualizationConsoleWrapper,
    [DisplayCalendarButton.defaultProps.uniqueActionButtonId]: CalendarWrapper,
    [DisplayNetworkGraphButton.defaultProps.uniqueActionButtonId]: AdvancedSearchNetworkGraph,
    [SaveToListButton.defaultProps.uniqueActionButtonId]: CreateListForm,
    [AddFileButton.defaultProps.uniqueActionButtonId]: CreateAttachmentForm,
    [UnconnectedBulkActionsButton.defaultProps.uniqueActionButtonId]: BulkActionsConsoleWrapper,
}

const UnconnectedSearchProfileInput = (props) => {
    const searchTextareaRef = useRef(null)

    return (
        <SearchTextarea
            hasBorder
            isSearchProfileTextarea
            searchButtonOnClick={() => {
                props.updateFilter(
                    AdvancedSearchFilter.defaultProps.uniqueFilterId,
                    props.uniqueFlvId,
                    searchTextareaRef.current.value,
                    Boolean(searchTextareaRef.current.value),
                )
            }}
            searchInputDefaultValue={props.filterValue}
            searchInputOnKeyDown={(event) => {
                if (event.key === "Enter") {
                    event.preventDefault()
                    props.updateFilter(
                        AdvancedSearchFilter.defaultProps.uniqueFilterId,
                        props.uniqueFlvId,
                        event.target.value,
                        Boolean(event.target.value),
                    )
                }
            }}
            searchInputPlaceholder="Search..."
            searchTextareaRef={searchTextareaRef}
        />
    )
}
const searchInputMapStateToProps = (state, props) => ({
    filterValue: selectFilterValue(state, {
        uniqueFlvId: props.uniqueFlvId,
        uniqueFilterId: AdvancedSearchFilter.defaultProps.uniqueFilterId,
    }),
})
export const SearchProfileInput = connect(searchInputMapStateToProps, { updateFilter })(UnconnectedSearchProfileInput)

const UnconnectedSearchCountInProfile = (props) => {
    const precisionMaxThreshold = getPrecisionMaxThreshold(props.quorumDataType)
    const displayLabel =
        props.currentCount === 1 ? getDataLabelSingular(props.quorumDataType) : getDataLabelPlural(props.quorumDataType)
    const getSearchHeaderSubtitle = () => {
        const searchHeaderSubtitle = [props.abbrevCount, displayLabel]

        if (props.isBulkActions && props.bulkActionsCount > 0) {
            searchHeaderSubtitle.push(` (${props.bulkActionsCount.toLocaleString()} Selected)`)
        }

        return searchHeaderSubtitle.join(" ")
    }
    // request the exact count if the estimated count is between the max and min threshold
    useEffect(() => {
        if (props.currentCount < precisionMaxThreshold && props.currentCount > PRECISION_MIN_THRESHOLD) {
            props.toggleSearchCountPrecision(
                props.currentExactCount,
                props.isExactCountInaccessible,
                props.shouldShowExactCount,
                DjangIO.newResourceFromDataType(props.quorumDataType),
                props.uniqueFlvId,
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.currentCount])

    return (
        <Tooltip
            label={
                props.currentCount <= precisionMaxThreshold
                    ? `${(props.currentExactCount || props.currentCount).toLocaleString()} ${displayLabel}`
                    : "Too many results to display exact count."
            }
            placement="top"
        >
            <div style={{ overflow: "hidden" }}>
                <SectionHeader title={!props.countIsLoading ? getSearchHeaderSubtitle() : "Counting..."} />
            </div>
        </Tooltip>
    )
}
const searchCountMapStateToProps = (state, props) => ({
    abbrevCount: selectAbbreviatedCurrentCount(state, props),
    bulkActionsCount: selectBulkActionsCount(state, props),
    countIsLoading: selectIsCountLoading(state, props),
    currentCount: selectCurrentCount(state, props),
    currentExactCount: selectCurrentExactCount(state, props),
    isBulkActions: selectIsBulkActionsSubmittable(state, props),
    isExactCountInaccessible: selectIsExactCountInaccessible(state, props),
    shouldShowExactCount: selectShouldShowExactCount(state, props),
    uniqueFlvId: props.uniqueFlvId,
})
UnconnectedSearchCountInProfile.propTypes = {
    abbrevCount: PropTypes.number,
    bulkActionsCount: PropTypes.number,
    countIsLoading: PropTypes.bool,
    currentCount: PropTypes.number,
    currentExactCount: PropTypes.number,
    isBulkActions: PropTypes.bool,
    isExactCountInaccessible: PropTypes.bool,
    quorumDataType: PropTypes.string,
    shouldShowExactCount: PropTypes.bool,
    toggleSearchCountPrecision: PropTypes.func,
    uniqueFlvId: PropTypes.string,
}
export const SearchCountInProfile = connect(searchCountMapStateToProps, { toggleSearchCountPrecision })(
    UnconnectedSearchCountInProfile,
)

const UnconnectedCreatePoliticalCommitteeButton = (props) =>
    genericActionButton({
        ...props,
        uniqueActionButtonId: "createPoliticalCommitteeButton",
        label: "New Political Committee",
        fa5_icon: "plus",
        fa5_icon_family: "far",
        icon: "fa-plus",
        callback: () => {
            const newCommitteeUrl = `/pacinternal/${props.ledgerSettingsId}/political_committees/new/`
            props.pushPage(newCommitteeUrl)
        },
        tooltipText: "Create a new political committee",
        styleClassName: "political-committees-create-new-committee",
    })
UnconnectedCreatePoliticalCommitteeButton.propTypes = {
    pushPage: PropTypes.func,
    ledgerSettingsId: PropTypes.number,
}

export const CreatePoliticalCommitteeButton = connect(
    (state) => ({ ledgerSettingsId: state.pac.getIn(["ledgerSettings", 0, "id"]) }),
    { pushPage },
)(UnconnectedCreatePoliticalCommitteeButton)
